import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ymdLocal } from '../lib/dates';
import api, { logout } from '../api';
import { useFeature } from '../hooks/useFeature';
import DocumentItemsPanel from '../components/DocumentItemsPanel';
import VatBreakdownPanel, { VatBreakdownRow } from '../components/VatBreakdownPanel';
import OmegaExportModal from '../components/OmegaExportModal';
import OutputInvoicesManager from '../components/OutputInvoicesManager';
import SignaturePad from '../components/SignaturePad';
import { parseChain, levelColor, levelFromRole, levelFromStatus, roleLabel as levelRoleLabel } from '../lib/levels';
import { pushState, enablePush, disablePush } from '../lib/push';
import DashboardSidebar, { DashView, DashCounts, SidebarLink, SidebarMonth } from '../components/DashboardSidebar';
import DocumentsTable from '../components/DocumentsTable';
import ApprovalTimeline from '../components/ApprovalTimeline';
import AccountSwitcher from '../components/AccountSwitcher';
import StickyTop from '../components/StickyTop';
import { LinkedAccount, switchAccount } from '../lib/accounts';
import '../dashboard.css';

// Nadpisy a prázdne stavy pohľadov ľavého panela.
const VIEW_TITLES: Record<DashView, { title: (role: string) => string; sub: string; empty: string }> = {
  queue: { title: r => r === 'accountant' ? 'Na vybavenie' : 'Na schválenie', sub: 'Doklady, ktoré čakajú na tvoju akciu.', empty: 'Nič na teba nečaká.' },
  review: { title: () => 'Kontrola', sub: 'Rozpor OCR, možný duplikát alebo doklad vrátený z vyššej úrovne.', empty: 'Žiadny doklad nepotrebuje kontrolu.' },
  mine: { title: () => 'Moje odoslané', sub: 'Doklady, ktoré si nahral alebo odfotil ty, za posledných 60 dní. Len na čítanie.', empty: 'Za posledných 60 dní si nenahral žiadny doklad.' },
  passed: { title: () => 'Prešlo cezo mňa', sub: 'Doklady, ktoré si schválil a ešte nie sú u účtovníčky. Len na čítanie.', empty: 'Všetko, čo si schválil, je už u účtovníčky.' },
  approved: { title: () => 'Schválené', sub: 'Posledných 30 dní. Staršie nájdeš v Admin → Archív.', empty: 'Za posledných 30 dní nebol schválený žiadny doklad.' },
  rejected: { title: () => 'Zamietnuté a vrátené', sub: 'Posledných 30 dní. Staršie nájdeš v Admin → Archív.', empty: 'Za posledných 30 dní nebol zamietnutý ani vrátený žiadny doklad.' },
};

const API = '/api';
const BASE = '';

interface Document {
  id: string;
  vendor: string;
  amount: number;
  currency: string;
  status: string;
  invoice_date: string;
  due_date: string | null;
  is_paid: boolean;
  invoice_number?: string | null;
  is_proforma?: boolean;
  stredisko?: string | null;
  zakazka?: string | null;
  ecv?: string | null;
  // Modul Objednávky — prepojenie došlej faktúry na internú objednávku
  order_id?: string | null;
  order_ref?: string | null;
  linked_order?: { order_number: string; subject: string | null; amount: any; currency: string | null; status: string } | null;
  description: string;
  original_filename: string;
  submitted_by_email: string;
  category: string;
  ocr_raw: string;
  file_path: string;
  created_at: string;
  email_message_id: string | null;
  auto_approved?: boolean;
  accountant_handled_at?: string | null;
  required_level?: 'l1' | 'l2' | 'l1_l2';
  // Snapshot "prečo táto úroveň" uložený pri nahratí (staré doklady: null).
  level_reason?: {
    kind: 'auto' | 'rule' | 'fallback';
    level?: string;
    rule_name?: string | null;
    rule_category?: string | null;
    amount_min?: number | null;
    amount_max?: number | null;
    amount?: number;
    category?: string | null;
  } | null;
  ocr_warning?: string | null;
  // Sledovanie: u koho doklad leží a odkedy (GET /documents).
  dispatched_at?: string | null;
  total_with_vat?: number | string | null;
  waiting_level?: number | null;
  waiting_on?: string[];
  waiting_since?: string | null;
  duplicate_of_id?: string | null;
  duplicate_reason?: string | null;
  duplicate_of?: { id: string; invoice_number?: string | null; vendor?: string | null; amount?: number | null; currency?: string | null; status?: string; created_at?: string; original_filename?: string | null } | null;
  total_amount?: number | string | null;
  vat_base_zero?: number | string | null;
  vat_base_neobsahuje?: number | string | null;
  vat_breakdown?: VatBreakdownRow[];
  // Faktúra so zlúčenými prílohami (výkaz, dodací list…) — originály súborov
  source_files?: { role: 'invoice' | 'attachment'; filename: string; pages?: number }[] | null;
}

interface MonthStats {
  month: string; // 'YYYY-MM'
  total: number;
  awaiting_l1: number;
  awaiting_l2: number;
  // Čakajúce po úrovniach {"1": 3, "2": 1, "3": 2} — generický náhrada za
  // dvojicu awaiting_l1/awaiting_l2 (tie ostávajú kvôli spätnej kompatibilite).
  awaiting_levels?: Record<string, number> | null;
  approved_count: number;
  approved_sum: number;
  currency: string | null;
  // Rozpad schválených súm per mena {"EUR": 250.5, "USD": 30}.
  // Rieši bug keď jedna USD faktúra nakazila celomesačný súčet.
  approved_sums?: Record<string, number> | null;
}

// Náhľad vrátenia na opravu (GET /documents/:id/return-preview).
//   mode 'level' — doklad sa vráti o úroveň nižšie, stačí dôvod
//   mode 'email' — nižšia úroveň neexistuje, odpoveď ide odosielateľovi
interface ReturnPreview {
  mode: 'level' | 'email';
  current_level: number;
  target_level?: number;
  to?: string | null;
  can_email?: boolean;
  subject?: string;
  body?: string;
  threaded?: boolean;
}

interface ReturnModalState {
  preview: ReturnPreview | null;
  loading: boolean;
  reason: string;
  subject: string;
  body: string;
  // Len čo si schvaľovateľ text upraví, prestaneme ho prepisovať pri
  // každom písmene v dôvode — inak by mu úpravy miznú pod rukami.
  bodyDirty: boolean;
}

interface HistoryEntry {
  id: string;
  action: string;
  level: number;
  comment: string;
  user_name: string;
  user_email: string;
  created_at: string;
}

// Vypínač hromadného schvaľovania. Dočasne OFF (user request 2026-07-03) —
// backend /documents/bulk-action ostáva, len skryjeme UI. Zapnutie = true.
const BULK_ACTIONS_ENABLED = false;

export default function Dashboard() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [stats, setStats] = useState<MonthStats[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const hasResaleDetection = useFeature('resale_detection');
  const hasOrders = useFeature('orders');
  const hasInvoicing = useFeature('invoicing');
  const hasOmegaExport = useFeature('omega_export');
  const hasFinance = useFeature('finance_overview');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [stampStredisko, setStampStredisko] = useState('');
  const [stampZakazka, setStampZakazka] = useState('');
  const [stampEcv, setStampEcv] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // Vrátenie na opravu. Z nižšej úrovne nie je komu doklad podať ďalej dole,
  // tak sa posiela emailová odpoveď odosielateľovi — preview povie, komu a s
  // akým textom, a schvaľovateľ ho môže prepísať pred odoslaním.
  const [returnModal, setReturnModal] = useState<ReturnModalState | null>(null);
  const [returnSaving, setReturnSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({ vendor: '', amount: '', currency: 'EUR', invoice_date: '', category: '', description: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showOmegaExport, setShowOmegaExport] = useState(false);
  const [showOutputInvoices, setShowOutputInvoices] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{done: number, total: number} | null>(null);
  // sha256 nahratých súborov, ktoré ešte rozpoznáva OCR na pozadí (server odpovedá 202).
  const [processing, setProcessing] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [fileAvailable, setFileAvailable] = useState<boolean | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Mobil: PDF ako obrázky strán (iOS iframe nevie fit-to-width ani pinch zoom).
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [pdfPagesState, setPdfPagesState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Web Push na tomto zariadení ('on' | 'off' | 'unsupported' | 'denied')
  const [pushSt, setPushSt] = useState<'on' | 'off' | 'unsupported' | 'denied'>('unsupported');
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { pushState().then(setPushSt).catch(() => {}); }, []);
  // Session-only prepínače "Zobraziť aj schválené / zamietnuté / vybavené
  // faktúry" — useState → po odhlásení/refresh sa zase vypnú (presne čo user
  // chce). showHandled je relevantný len pre rolu accountant (B8).
  const [showHandled, setShowHandled] = useState(false);
  // Pohľad z ľavého panela (fronta / kontrola / sledovanie) + počítadlá.
  const [view, setView] = useState<DashView>('queue');
  const [counts, setCounts] = useState<DashCounts>({});
  // Prepojené účty (iné okruhy tej istej osoby) — pre ponuku "ďalší okruh".
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  // Vyhľadávanie + stránkovanie (P0 z auditu). search filtruje dodávateľa/číslo
  // faktúry/súbor; pageSize je aktuálny strop načítaných dokladov ("Načítať ďalšie").
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  // Hromadný výber dokladov na schválenie/zamietnutie naraz.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [handleSaving, setHandleSaving] = useState(false);
  const [proformaSaving, setProformaSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  // Osobný podpis schvaľovateľa — kreslí sa do pečiatky na schválené PDF
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [mySigExists, setMySigExists] = useState(false);
  // Manuálne prepojenie dokladu s internou objednávkou (modul Objednávky):
  // návrhy podľa dodávateľa + server-side vyhľadávanie — objednávok môžu byť
  // tisíce, celý zoznam sa nikdy neťahá
  const [linkSuggested, setLinkSuggested] = useState<any[] | null>(null); // null = panel zatvorený
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState<any[]>([]);
  const [linkSaving, setLinkSaving] = useState(false);
  // Pripojenie dokladu ako prílohy k inej faktúre (zlúči sa do jej PDF)
  const [attachCandidates, setAttachCandidates] = useState<any[] | null>(null);
  const [attachQuery, setAttachQuery] = useState('');
  const [attachSaving, setAttachSaving] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDocuments();
    loadStats();
    api.get('/auth/tenant-meta').then(r => {
      const { logo_url, features } = r.data;
      if (logo_url) setLogoUrl(logo_url);
      // Aktualizuj feature flag cache (používa sa cez useFeature() hook)
      import('../hooks/useFeature').then(m => m.setTenantFeatures(features || {}));
    }).catch(() => {});
  }, []);

  // Po načítaní dokladov — ak je v URL ?doc=<id>, otvor ten doklad
  useEffect(() => {
    if (documents.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const docId = params.get('doc');
    if (docId) {
      const found = documents.find(d => d.id === docId);
      if (found) {
        setSelectedDoc(found);
        // Vyčisti URL aby sa modal znova neotváral pri obnove
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [documents]);

  useEffect(() => {
    document.body.style.overflow = selectedDoc ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedDoc]);

  // Po otvorení detailu — natiahni súbor cez autentifikovaný endpoint ako blob.
  // Token ide v Authorization hlavičke (cez api interceptor), nie v URL, takže
  // sa neleakuje do logov/histórie a súbor je tenant-scoped na backende.
  useEffect(() => {
    if (!selectedDoc?.id || !selectedDoc?.file_path) { setFileAvailable(null); setPreviewUrl(null); return; }
    let objectUrl: string | null = null;
    let cancelled = false;
    setFileAvailable(null); // loading
    setPreviewUrl(null);
    api.get(`/documents/${selectedDoc.id}/file`, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPreviewUrl(objectUrl);
        setFileAvailable(true);
      })
      .catch(() => { if (!cancelled) setFileAvailable(false); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedDoc]);

  // Mobil + PDF: strany ako JPEG z /preview?page=N (max 8), na šírku obrazovky,
  // celý modal scrolluje ako jedna stránka a dá sa pinch-zoomovať.
  useEffect(() => {
    const urls: string[] = [];
    let cancelled = false;
    setPdfPages([]);
    setPdfPagesState('idle');
    if (!isMobile || !selectedDoc?.id || !selectedDoc?.file_path || !isPdf(selectedDoc.original_filename) || fileAvailable !== true) return;
    setPdfPagesState('loading');
    (async () => {
      try {
        const first = await api.get(`/documents/${selectedDoc.id}/preview`, { params: { page: 1 }, responseType: 'blob' });
        if (cancelled) return;
        urls.push(URL.createObjectURL(first.data));
        setPdfPages([...urls]);
        const count = Math.min(8, parseInt(String(first.headers['x-page-count'] || '1'), 10) || 1);
        for (let n = 2; n <= count; n++) {
          const r = await api.get(`/documents/${selectedDoc.id}/preview`, { params: { page: n }, responseType: 'blob' });
          if (cancelled) return;
          urls.push(URL.createObjectURL(r.data));
          setPdfPages([...urls]);
        }
        setPdfPagesState('done');
      } catch {
        if (!cancelled) setPdfPagesState('error');
      }
    })();
    return () => { cancelled = true; urls.forEach(u => URL.revokeObjectURL(u)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, selectedDoc?.id, fileAvailable]);

  // Môže aktuálny user schváliť/zamietnuť TENTO doklad? Zdieľané pre hromadný
  // výber aj otvorený detail — jediný zdroj pravdy pre "smiem konať".
  const canActOn = (doc: Document): boolean => {
    const docLevel = levelFromStatus(doc.status);
    if (docLevel === null) return false;
    return levelFromRole(user.role) === docLevel || user.role === 'admin' || !!user.isSuperAdmin;
  };

  const loadDocuments = async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    try {
      const params: Record<string, number | string> = { limit: pageSize };
      params.view = view;
      if (view === 'queue' && showHandled) params.show_handled = 1;
      // Počítadlá ľavého panela — popri zozname, chyba nesmie zhodiť zoznam.
      api.get('/documents/counts').then(r => setCounts(r.data || {})).catch(() => {});
      if (search.trim()) params.search = search.trim();
      const response = await api.get(`/documents`, { params });
      const rows = Array.isArray(response.data) ? response.data : [];
      setDocuments(rows);
      setHasMore(rows.length >= pageSize);
      // Vyčisti z výberu doklady, ktoré už v zozname nie sú alebo sa už nedajú
      // schvaľovať (napr. medzitým spracované) — bez rušenia stále platných.
      setSelectedIds(prev => prev.filter(id => rows.some((d: Document) => d.id === id && canActOn(d))));
    } catch (err: any) {
      // Background poll (B17): pri transientnej chybe NEvymaž zoznam ani
      // neukazuj error — používateľ pri otvorenom dashboarde nesmie prísť
      // o doklady kvôli jednému zlyhanému refreshu.
      if (!background) {
        setError('Chyba pri načítaní dokumentov');
        setDocuments([]);
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  // Re-load keď user prepne filtre alebo klikne "Načítať ďalšie" (pageSize).
  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, showHandled, pageSize]);

  // Vyhľadávanie s debounce — nereloaduje pri každom písmene. Preskočí prvý
  // mount (o počiatočné načítanie sa stará efekt vyššie).
  const searchMounted = React.useRef(false);
  useEffect(() => {
    if (!searchMounted.current) { searchMounted.current = true; return; }
    const t = setTimeout(() => loadDocuments(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // B17: automatický refresh zoznamu, aby sa nové doklady (z email pollingu)
  // zobrazili bez manuálneho reloadu stránky. Polling, nie WebSocket — len
  // frontend, žiadny zásah do backendu. Pauzne keď je otvorený detail dokladu
  // (neruší rozpracované schvaľovanie/pečiatkové polia) alebo keď tab nie je
  // viditeľný. Interval sa re-vytvára pri zmene filtrov, aby refetch rešpektoval
  // aktuálne zvolené filtre (closure nad showApproved/...).
  useEffect(() => {
    if (selectedDoc) return; // otvorený modal → nepolluj
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return; // skrytý tab → preskoč
      loadDocuments({ background: true });
      loadStats({ background: true });
    }, 45000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, showHandled, selectedDoc, search, pageSize]);

  // B8: účtovník označí zamietnutý doklad ako vybavený (alebo zruší).
  const toggleHandled = async (handled: boolean) => {
    if (!selectedDoc) return;
    setHandleSaving(true);
    try {
      const response = await api.post(`/documents/${selectedDoc.id}/accountant-handle`, { handled });
      setSelectedDoc(response.data);
      // Reload zoznamu — keď default filter (showHandled=false) skryje
      // doklad, modal ostane otvorený s aktuálnym detailom kým user nezavrie.
      loadDocuments();
      // Reload histórie aby user hneď videl nový stamp (kto a kedy vybavil).
      try {
        const histRes = await api.get(`/documents/${selectedDoc.id}/history`);
        setHistory(histRes.data);
      } catch {/* tichá chyba — list a stav už updatnuté */}
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri ukladaní stavu');
    } finally {
      setHandleSaving(false);
    }
  };

  // B15: označ/odznač doklad ako nedaňový (proforma) → mimo Omega exportu.
  const toggleProforma = async (isProforma: boolean) => {
    if (!selectedDoc) return;
    setProformaSaving(true);
    try {
      const response = await api.post(`/documents/${selectedDoc.id}/proforma`, { is_proforma: isProforma });
      setSelectedDoc(response.data);
      loadDocuments();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri ukladaní príznaku');
    } finally {
      setProformaSaving(false);
    }
  };

  const loadStats = async (opts?: { background?: boolean }) => {
    try {
      const response = await api.get(`/documents/stats`);
      setStats(response.data);
    } catch (err) {
      // Background poll (B17): nevymaž počítadlá pri transientnej chybe.
      if (opts?.background !== true) setStats([]);
    }
  };

  const openModal = async (doc: Document) => {
    setSelectedDoc(doc);
    setActionComment('');
    setHistoryLoading(true);
    try {
      const [detailRes, historyRes] = await Promise.all([
        api.get(`/documents/${doc.id}`),
        api.get(`/documents/${doc.id}/history`)
      ]);
      setSelectedDoc(detailRes.data);
      setStampStredisko(detailRes.data.stredisko || '');
      setStampZakazka(detailRes.data.zakazka || '');
      setStampEcv(detailRes.data.ecv || '');
      setHistory(historyRes.data);
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Prepojenie/odpojenie dokladu s internou objednávkou (modul Objednávky)
  const resetLinkPanel = () => { setLinkSuggested(null); setLinkQuery(''); setLinkResults([]); };
  // Návrhy: schválené neuzavreté objednávky rovnakého dodávateľa (IČO presne,
  // inak kmeň názvu bez právnej formy), najnovšie prvé
  const loadLinkSuggestions = async (doc: any) => {
    try {
      const vendorQ = String(doc.vendor || '').split(/[,(]/)[0]
        .replace(/[\s,]+(s\.?\s?r\.?\s?o\.?|a\.?\s?s\.?|spol\..*)\s*$/i, '').trim();
      const params: any = { status: 'approved', open: '1', limit: 5 };
      if (doc.vendor_ico) params.vendor_ico = doc.vendor_ico;
      if (vendorQ) params.vendor_q = vendorQ;
      const r = await api.get('/orders', { params });
      setLinkSuggested(r.data || []);
    } catch {
      setLinkSuggested([]);
    }
  };
  // vyhľadávanie s debouncom — hľadá server (číslo OBJ, dodávateľ, predmet)
  useEffect(() => {
    if (linkSuggested === null) return;
    const q = linkQuery.trim();
    if (q.length < 2) { setLinkResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/orders', { params: { status: 'approved', open: '1', q, limit: 20 } });
        setLinkResults(r.data || []);
      } catch {
        setLinkResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [linkQuery, linkSuggested]);
  // pri prepnutí dokladu panel zavri, nech sa neukazujú návrhy k inému dokladu
  useEffect(() => { resetLinkPanel(); }, [selectedDoc?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const linkOrder = async (docId: string, orderId: string | null) => {
    setLinkSaving(true);
    try {
      await api.post(`/documents/${docId}/link-order`, { order_id: orderId });
      // refresh detailu — linked_order dopĺňa detail endpoint
      const detailRes = await api.get(`/documents/${docId}`);
      setSelectedDoc(detailRes.data);
      resetLinkPanel();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Prepojenie zlyhalo');
    } finally {
      setLinkSaving(false);
    }
  };

  // Návrhy faktúr: z toho istého mailu prvé, inak najnovšie; q hľadá dodávateľa/číslo/súbor
  const loadAttachCandidates = async (docId: string, q = '') => {
    try {
      const r = await api.get(`/documents/${docId}/attach-candidates`, { params: q ? { q } : {} });
      setAttachCandidates(r.data || []);
    } catch {
      setAttachCandidates([]);
    }
  };
  useEffect(() => {
    if (attachCandidates === null || !selectedDoc) return;
    const q = attachQuery.trim();
    if (q.length === 1) return;
    const t = setTimeout(() => loadAttachCandidates(selectedDoc.id, q), 300);
    return () => clearTimeout(t);
  }, [attachQuery]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAttachCandidates(null); setAttachQuery(''); }, [selectedDoc?.id]);
  const attachTo = async (target: any) => {
    if (!selectedDoc) return;
    const label = `${target.vendor || target.original_filename}${target.invoice_number ? ` č. ${target.invoice_number}` : ''}`;
    if (!window.confirm(`Pripojiť tento doklad za strany faktúry ${label}? Samostatný doklad zanikne.`)) return;
    setAttachSaving(true);
    try {
      await api.post(`/documents/${selectedDoc.id}/attach-to`, { target_id: target.id });
      loadDocuments();
      await openModal({ id: target.id } as Document);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Pripojenie zlyhalo');
    } finally {
      setAttachSaving(false);
    }
  };

  const closeModal = () => {
    setSelectedDoc(null);
    setHistory([]);
    setActionComment('');
    setStampStredisko('');
    setStampZakazka('');
    setStampEcv('');
    setEditing(false);
  };

  const startEdit = () => {
    if (!selectedDoc) return;
    setEditFields({
      vendor: selectedDoc.vendor || '',
      amount: selectedDoc.amount ? String(selectedDoc.amount) : '',
      currency: selectedDoc.currency || 'EUR',
      invoice_date: ymdLocal(selectedDoc.invoice_date),
      category: selectedDoc.category || '',
      description: selectedDoc.description || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!selectedDoc) return;
    setEditSaving(true);
    try {
      const response = await api.patch(`/documents/${selectedDoc.id}`, editFields);
      setSelectedDoc(response.data);
      setEditing(false);
      loadDocuments();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri ukladaní');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteDocument = async () => {
    if (!selectedDoc) return;
    if (!window.confirm('Naozaj chceš vymazať tento doklad? Táto akcia je nevratná.')) return;
    try {
      await api.delete(`/documents/${selectedDoc.id}`);
      closeModal();
      loadDocuments();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri mazaní dokladu');
    }
  };

  const handleAction = async (action: string) => {
    if (!selectedDoc) return;
    setActionLoading(true);
    try {
      await api.post(`/documents/${selectedDoc.id}/action`, {
        action,
        comment: actionComment,
        stredisko: stampStredisko,
        zakazka: stampZakazka,
        ecv: stampEcv
      });
      closeModal();
      loadDocuments();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Vrátenie na opravu ─────────────────────────────────────────────────────
  // Dôvod sa v režime 'email' premieta do textu odpovede, kým ho užívateľ
  // sám neprepíše. Server generuje ten istý text, takže náhľad nie je fikcia.
  const returnBodyWithReason = (template: string, reason: string) =>
    template.replace(/^Dôvod: .*$/m, `Dôvod: ${reason.trim() || 'neuvedený'}`);

  const openReturnModal = async () => {
    if (!selectedDoc) return;
    setReturnModal({ preview: null, loading: true, reason: '', subject: '', body: '', bodyDirty: false });
    try {
      const res = await api.get(`/documents/${selectedDoc.id}/return-preview`);
      const p: ReturnPreview = res.data;
      setReturnModal({
        preview: p,
        loading: false,
        reason: '',
        subject: p.subject || '',
        body: p.body || '',
        bodyDirty: false,
      });
    } catch (err: any) {
      setReturnModal(null);
      alert(err.response?.data?.error || 'Chyba pri príprave vrátenia');
    }
  };

  const setReturnReason = (reason: string) => {
    setReturnModal(m => {
      if (!m) return m;
      const body = m.preview?.mode === 'email' && !m.bodyDirty
        ? returnBodyWithReason(m.preview.body || '', reason)
        : m.body;
      return { ...m, reason, body };
    });
  };

  const submitReturn = async () => {
    if (!selectedDoc || !returnModal || !returnModal.reason.trim()) return;
    setReturnSaving(true);
    try {
      const res = await api.post(`/documents/${selectedDoc.id}/action`, {
        action: 'return',
        comment: returnModal.reason,
        ...(returnModal.preview?.mode === 'email'
          ? { returnEmail: { subject: returnModal.subject, body: returnModal.body } }
          : {}),
      });
      setReturnModal(null);
      closeModal();
      loadDocuments();
      loadStats();
      if (res.data?.message) alert(res.data.message);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri vrátení dokladu');
    } finally {
      setReturnSaving(false);
    }
  };

  // ── Hromadný výber a hromadná akcia ────────────────────────────────────────
  const selectableDocs = documents.filter(canActOn);
  const allSelected = selectableDocs.length > 0 && selectedIds.length === selectableDocs.length;
  const toggleSelect = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? [] : selectableDocs.map(d => d.id));

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    if (selectedIds.length === 0) return;
    const verb = action === 'approve' ? 'schváliť' : 'zamietnuť';
    if (!window.confirm(`Naozaj ${verb} ${selectedIds.length} vybraných dokladov?`)) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/documents/bulk-action', { ids: selectedIds, action });
      const { succeeded = 0, failed = 0 } = res.data || {};
      if (failed) alert(`Spracované: ${succeeded}, zlyhalo: ${failed}.`);
      setSelectedIds([]);
      loadDocuments();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Chyba pri hromadnej akcii');
    } finally {
      setBulkLoading(false);
    }
  };

  // Esc zavrie otvorený detail dokladu (očakávaný štandard, dovtedy chýbal).
  useEffect(() => {
    if (!selectedDoc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc]);

  const formatMonth = (yyyyMM: string) => {
    const [year, month] = yyyyMM.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString('sk-SK', { month: 'long', year: 'numeric' });
  };

  const currentMonth = ymdLocal(new Date()).slice(0, 7);

  const handleLogout = () => logout();

  const generatePassword = () => {
    const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specials = '!@#$%^&*()-_=+';
    const all = letters + digits + specials;
    let pwd = '';
    pwd += letters[Math.floor(Math.random() * letters.length)];
    pwd += digits[Math.floor(Math.random() * digits.length)];
    pwd += specials[Math.floor(Math.random() * specials.length)];
    for (let i = 0; i < 9; i++) pwd += all[Math.floor(Math.random() * all.length)];
    return pwd.split('').sort(() => Math.random() - 0.5).join('');
  };

  const submitPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage('');
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordMessage('Nové heslo a potvrdenie sa nezhodujú');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put('/users/me/password', { currentPassword: passwordForm.current, newPassword: passwordForm.next });
      setPasswordMessage('✓ Heslo zmenené');
      setPasswordForm({ current: '', next: '', confirm: '' });
      setTimeout(() => { setShowPasswordModal(false); setPasswordMessage(''); }, 1500);
    } catch (err: any) {
      setPasswordMessage('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setPasswordSaving(false);
    }
  };

  // Zatvor dropdown pri kliknutí mimo
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = () => setUserMenuOpen(false);
    setTimeout(() => window.addEventListener('click', handler), 0);
    return () => window.removeEventListener('click', handler);
  }, [userMenuOpen]);

  const submitUpload = async () => {
    if (uploadFiles.length === 0) return;
    const filesToUpload = [...uploadFiles];
    setShowUpload(false);
    setUploadFiles([]);
    setUploading(true);
    setUploadProgress({ done: 0, total: filesToUpload.length });
    let errors: string[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        const formData = new FormData();
        formData.append('file', filesToUpload[i]);
        const r = await api.post(`/documents/upload`, formData);
        const sha = r.data?.sha256;
        if (sha) setProcessing(p => [...p, sha]);
      } catch (err: any) {
        errors.push(`${filesToUpload[i].name}: ${err.response?.data?.error || 'Chyba'}`);
      }
      setUploadProgress({ done: i + 1, total: filesToUpload.length });
    }
    setUploading(false);
    setUploadProgress(null);
    if (errors.length > 0) alert('Niektoré súbory sa nepodarilo nahrať:\n' + errors.join('\n'));
  };

  // OCR beží na pozadí — každých 5 s sa pozrieme, či už doklady vznikli, a keď
  // áno, potichu obnovíme zoznam. Po 5 min to vzdáme (doklad sa objaví pri
  // ďalšom načítaní). Ref, aby interval volal aktuálny pohľad, nie ten zo štartu.
  const processingRef = React.useRef(processing);
  processingRef.current = processing;
  const loadDocumentsRef = React.useRef(loadDocuments);
  loadDocumentsRef.current = loadDocuments;
  const isProcessing = processing.length > 0;
  useEffect(() => {
    if (!isProcessing) return;
    const started = Date.now();
    const timer = window.setInterval(async () => {
      if (Date.now() - started > 5 * 60 * 1000) { setProcessing([]); return; }
      try {
        const r = await api.get('/documents/mine', { params: { limit: 50 } });
        const done = new Set((r.data || []).map((d: { sha256_hash?: string }) => d.sha256_hash));
        const left = processingRef.current.filter(sha => !done.has(sha));
        if (left.length < processingRef.current.length) {
          setProcessing(left);
          loadDocumentsRef.current({ background: true });
        }
      } catch { /* skúsime o 5 s znova */ }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [isProcessing]);

  const isImage = (filename: string) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png'].includes(ext || '');
  };

  const isPdf = (filename: string) => {
    return filename?.split('.').pop()?.toLowerCase() === 'pdf';
  };

  const roleLabel = (role: string) => {
    const lvl = levelFromRole(role);
    if (lvl !== null) return levelRoleLabel(lvl);
    switch (role) {
      case 'admin': return 'Admin';
      case 'accountant': return 'Účtovník';
      case 'submitter': return 'Zberač dokladov';
      case 'viewer': return 'Pozorovateľ';
      default: return role;
    }
  };

  const daysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const statusColor = (status: string) => {
    const lvl = levelFromStatus(status);
    if (lvl !== null) return levelColor(lvl);
    switch (status) {
      case 'approved': return '#16a34a';
      case 'rejected': return '#dc2626';
      case 'returned': return '#b45309';
      default: return '#6b7280';
    }
  };

  // Doklad čakajúci na L1 s reťazcom 'l1_l2_l3' ukáže "Čaká L1 → L2 → L3",
  // aby bolo na prvý pohľad jasné, koľko podpisov ho ešte čaká.
  const statusLabel = (status: string, requiredLevel?: string) => {
    const lvl = levelFromStatus(status);
    if (lvl !== null) {
      // Bez známeho reťazca (staršie doklady) ukáž len aktuálnu úroveň —
      // fallback reťazca by tvrdil, že doklad čaká aj na L2, čo nemusí byť pravda.
      if (!requiredLevel) return `Čaká L${lvl}`;
      const remaining = Array.from(new Set([lvl, ...parseChain(requiredLevel).filter(n => n > lvl)])).sort((a, b) => a - b);
      return `Čaká ${remaining.map(n => `L${n}`).join(' → ')}`;
    }
    switch (status) {
      case 'approved': return 'Schválené';
      case 'rejected': return 'Zamietnuté';
      case 'returned': return 'Vrátené na opravu';
      default: return status;
    }
  };

  // Ľudsky čitateľné vysvetlenie z level_reason snapshotu (prečo doklad
  // spadol na túto úroveň schvaľovania). Staré doklady bez snapshotu → null.
  const levelReasonText = (doc: Document): string | null => {
    const r = doc.level_reason;
    if (!r) return null;
    const lvl = (l?: string) => {
      const chain = parseChain(l);
      return chain.length === 1 ? `iba L${chain[0]}` : chain.map(n => `L${n}`).join(' + ');
    };
    const eur = (n?: number | null) => `${Number(n ?? 0).toLocaleString('sk-SK')} €`;
    if (r.kind === 'auto') {
      return `Doklad bol schválený automaticky pravidlom „${r.rule_name || 'bez názvu'}" — bez ľudského schvaľovania.`;
    }
    if (r.kind === 'rule') {
      const cat = r.rule_category ? `kategória „${r.rule_category}"` : 'ľubovoľná kategória';
      const range = r.amount_max != null
        ? `suma ${eur(r.amount_min)} – ${eur(r.amount_max)}`
        : `suma od ${eur(r.amount_min)} (bez horného limitu)`;
      return `Úroveň ${lvl(r.level)} podľa pravidla: ${cat}, ${range}. Doklad: ${r.category ? `„${r.category}"` : 'bez kategórie'}, ${eur(r.amount)}.`;
    }
    // fallback — žiadne pravidlo nesedelo
    return `Žiadne schvaľovacie pravidlo nezodpovedalo dokladu (${r.category ? `kategória „${r.category}"` : 'bez kategórie'}, ${eur(r.amount)}) — použil sa bezpečný default L1 + L2. Pravidlo doplníš v Admin → Pravidlá.`;
  };

  const actionLabel = (action: string) => {
    switch (action) {
      case 'approved': return '✓ Schválené';
      case 'rejected': return '✗ Zamietnuté';
      case 'returned': return '↩ Vrátené na opravu';
      case 'accountant_handled': return '✓ Vybavené účtovníkom';
      case 'accountant_unhandled': return '↺ Vrátené medzi nevybavené';
      default: return action;
    }
  };

  const canAct = !!selectedDoc && canActOn(selectedDoc);

  // Ľavý panel: odkazy na moduly, ktoré používateľ má (rovnaké podmienky ako Home).
  const isAdminUser = user.role === 'admin' || !!user.isSuperAdmin;
  const canAccounting = isAdminUser || user.role === 'accountant';
  const sidebarLinks: SidebarLink[] = [
    ...(canAccounting && hasInvoicing ? [{ icon: '🧾', label: 'Fakturácia', href: '/invoices' }] : []),
    ...(canAccounting && hasOmegaExport ? [{ icon: '📚', label: 'Účtovníctvo', href: '/accounting' }] : []),
    ...(canAccounting && hasFinance ? [{ icon: '📈', label: 'Financie', href: '/finance' }] : []),
    ...(hasOrders ? [{ icon: '🛒', label: 'Objednávky', href: '/orders' }] : []),
    ...(hasResaleDetection && canAccounting ? [
      { icon: '📄', label: 'Vystavené faktúry', onClick: () => setShowOutputInvoices(true) },
      { icon: '📥', label: 'Export do Omegy', onClick: () => setShowOmegaExport(true) },
    ] : []),
    ...(isAdminUser ? [{ icon: '⚙️', label: 'Administrácia', href: '/admin' }] : []),
  ];

  // Aktuálny mesiac prvý (karta v paneli ukazuje len prvý); zvyšok od najnovšieho.
  // Bez toho by doklad s chybným dátumom v budúcnosti obsadil hlavnú kartu.
  const statsOrdered = [
    ...stats.filter(s => s.month === currentMonth),
    ...stats.filter(s => s.month !== currentMonth),
  ];
  const sidebarMonths: SidebarMonth[] = statsOrdered.map(s => {
    const levels = Array.from(new Set([
      1, 2,
      ...Object.keys(s.awaiting_levels || {}).map(k => parseInt(k, 10)).filter(n => Number.isInteger(n)),
    ])).sort((a, b) => a - b);
    const sums = s.approved_sums && Object.keys(s.approved_sums).length > 0
      ? Object.entries(s.approved_sums)
      : [[s.currency || 'EUR', Number(s.approved_sum)] as [string, number]];
    return {
      month: s.month,
      label: formatMonth(s.month),
      total: s.total,
      waiting: levels.map(n => ({
        level: n,
        count: n === 1 ? s.awaiting_l1 : n === 2 ? s.awaiting_l2 : (s.awaiting_levels?.[String(n)] || 0),
      })),
      approved: s.approved_count,
      sums: sums.map(([cur, amt]) => `${Number(amt).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`),
    };
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>

      {/* Header + prepínač okruhov — ukotvené hore */}
      <StickyTop>
      <div className="app-header" style={{ backgroundColor: 'white', padding: '1rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Klik na logo/názov → launcher modulov */}
        <a href="/home" title="Späť na výber modulov" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
          <span style={{ color: '#6b7280', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>← Moduly</span>
          {logoUrl && <img src={`${BASE}${logoUrl}`} alt="logo" style={{ height: '44px', width: 'auto', objectFit: 'contain', flexShrink: 0 }} />}
          <h1 style={{ color: '#1f2937', margin: 0, fontSize: '1.25rem', lineHeight: '1.3' }}>Schvaľovanie<br />faktúr</h1>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setUserMenuOpen(o => !o); }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem 0.6rem',
              borderRadius: '4px', backgroundColor: userMenuOpen ? '#f3f4f6' : 'transparent'
            }}
          >
            <span style={{ color: '#1f2937', fontWeight: 500, fontSize: '0.9rem' }}>
              {user.first_name} {user.last_name} <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>▾</span>
            </span>
            <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{roleLabel(user.role)}</span>
          </button>
          {userMenuOpen && (
            <div
              className="user-menu"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                backgroundColor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderRadius: '6px', minWidth: '180px', overflow: 'hidden', zIndex: 100
              }}
            >
              {user.isSuperAdmin && (
                <button onClick={() => window.location.href = '/superadmin'}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontWeight: 500 }}>
                  Super Admin
                </button>
              )}
              {user.role === 'admin' && (
                <button onClick={() => window.location.href = '/admin'}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontWeight: 500 }}>
                  Admin panel
                </button>
              )}
              {pushSt !== 'unsupported' && (
                <button disabled={pushBusy || pushSt === 'denied'} onClick={async () => {
                  setPushBusy(true);
                  try {
                    if (pushSt === 'on') { await disablePush(); }
                    else { await enablePush(); }
                    setPushSt(await pushState());
                  } catch (e: any) {
                    alert(e?.message || 'Notifikácie sa nepodarilo zapnúť');
                    setPushSt(await pushState());
                  } finally { setPushBusy(false); }
                }}
                  title={pushSt === 'denied' ? 'Notifikácie sú zablokované v nastaveniach prehliadača' : 'Push notifikácie na nové doklady na tomto zariadení'}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: pushSt === 'denied' ? 'default' : 'pointer', color: pushSt === 'denied' ? '#9ca3af' : '#1f2937' }}>
                  {pushBusy ? 'Pracujem…'
                    : pushSt === 'on' ? '🔔 Notifikácie: zapnuté'
                    : pushSt === 'denied' ? '🔕 Notifikácie zablokované'
                    : '🔕 Zapnúť notifikácie'}
                </button>
              )}
              <button onClick={() => { setShowPasswordModal(true); setUserMenuOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1f2937' }}>
                Zmena hesla
              </button>
              {(levelFromRole(user.role) !== null || user.role === 'admin' || user.isSuperAdmin) && (
                <button onClick={() => {
                  setUserMenuOpen(false);
                  // Zisti či podpis existuje (kvôli tlačidlu Odstrániť v modale)
                  api.get('/settings/my-signature', { responseType: 'blob' })
                    .then(() => setMySigExists(true))
                    .catch(() => setMySigExists(false))
                    .finally(() => setShowSignatureModal(true));
                }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1f2937' }}>
                  Môj podpis
                </button>
              )}
              <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
              <button onClick={handleLogout}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 500 }}>
                Odhlásiť
              </button>
            </div>
          )}
        </div>
      </div>
      <AccountSwitcher onLoaded={setLinkedAccounts} />
      </StickyTop>

      {/* Modal osobného podpisu — kreslí sa do schvaľovacej pečiatky */}
      {showSignatureModal && (
        <SignaturePad
          endpoint="/settings/my-signature"
          title="Môj podpis"
          description="Podpíš sa prstom (na mobile či tablete), stylusom alebo myšou. Podpis sa pridá do schvaľovacej pečiatky na doklady, ktoré schváliš."
          hasExisting={mySigExists}
          onClose={() => setShowSignatureModal(false)}
          onSaved={() => setShowSignatureModal(false)}
        />
      )}

      {/* Modal zmeny hesla */}
      {showPasswordModal && (
        <div onClick={() => setShowPasswordModal(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', width: '100%', maxWidth: '400px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 1rem', color: '#1f2937' }}>Zmena hesla</h2>
            <form onSubmit={submitPasswordChange}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#374151' }}>Aktuálne heslo</label>
                <input type="password" required value={passwordForm.current} onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#374151' }}>Nové heslo</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input type="text" required value={passwordForm.next} onChange={e => setPasswordForm({ ...passwordForm, next: e.target.value })}
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box', fontFamily: 'monospace' }} />
                  <button type="button" onClick={() => {
                    const pwd = generatePassword();
                    setPasswordForm(prev => ({ ...prev, next: pwd, confirm: pwd }));
                  }}
                    style={{ padding: '0.4rem 0.75rem', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    Generovať
                  </button>
                </div>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#9ca3af' }}>Min. 8 znakov, veľké aj malé písmeno a číslo</p>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#374151' }}>Potvrdiť nové heslo</label>
                <input type="password" required value={passwordForm.confirm} onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
              </div>
              {passwordMessage && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: passwordMessage.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{passwordMessage}</p>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowPasswordModal(false)}
                  style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                  Zrušiť
                </button>
                <button type="submit" disabled={passwordSaving}
                  style={{ padding: '0.5rem 1rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: passwordSaving ? 0.6 : 1 }}>
                  {passwordSaving ? 'Ukladám...' : 'Uložiť'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content — ľavý panel (pohľady) + tabuľka dokladov */}
      <div className="dash-layout">
        <DashboardSidebar
          view={view}
          counts={counts}
          // Klik na už aktívny pohľad nezmení `view`, takže efekt načítania sa
          // nespustí — treba načítať ručne, inak ostane visieť "Načítavam...".
          onView={(v) => { setSelectedIds([]); setLoading(true); if (v === view) loadDocuments(); else setView(v); }}
          queueLabel={user.role === 'accountant' ? 'Na vybavenie' : 'Na schválenie'}
          links={sidebarLinks}
          months={sidebarMonths}
        />

        <div className="dash-main">
          <div className="dash-toolbar">
            <div>
              <h2>{VIEW_TITLES[view].title(user.role)}</h2>
              <p className="dash-toolbar-sub">{VIEW_TITLES[view].sub}</p>
            </div>
            <div className="header-buttons" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="dash-btn is-outline" onClick={() => { window.location.href = '/capture'; }}>
                📷 Nasnímať doklad
              </button>
              {(user.role === 'admin' || user.isSuperAdmin) && (
                <button className="dash-btn is-primary" onClick={() => setShowUpload(true)}>+ Nahrať doklad</button>
              )}
            </div>
          </div>

          {error && (
            <div style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="search"
              className="dash-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Hľadať dodávateľa, číslo faktúry, súbor…"
            />
            {BULK_ACTIONS_ENABLED && selectableDocs.length > 0 && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                <span>Vybrať všetky ({selectableDocs.length})</span>
              </label>
            )}
            {/* B8: "Zobraziť aj vybavené" — len pre účtovníka vo fronte. */}
            {user.role === 'accountant' && view === 'queue' && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={showHandled} onChange={e => setShowHandled(e.target.checked)} />
                <span>Zobraziť aj vybavené</span>
              </label>
            )}
          </div>

          {/* Hromadná akcia — zobrazí sa keď je aspoň jeden doklad vybraný. */}
          {BULK_ACTIONS_ENABLED && selectedIds.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem', padding: '0.6rem 0.9rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <strong style={{ color: '#1e40af' }}>Vybraných: {selectedIds.length}</strong>
              <button onClick={() => handleBulkAction('approve')} disabled={bulkLoading}
                style={{ padding: '0.45rem 0.9rem', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: bulkLoading ? 'default' : 'pointer', fontWeight: 600, opacity: bulkLoading ? 0.6 : 1 }}>
                ✓ Schváliť vybrané
              </button>
              <button onClick={() => handleBulkAction('reject')} disabled={bulkLoading}
                style={{ padding: '0.45rem 0.9rem', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: bulkLoading ? 'default' : 'pointer', fontWeight: 600, opacity: bulkLoading ? 0.6 : 1 }}>
                ✗ Zamietnuť vybrané
              </button>
              <button onClick={() => setSelectedIds([])} disabled={bulkLoading}
                style={{ padding: '0.45rem 0.9rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}>
                Zrušiť výber
              </button>
            </div>
          )}

          {loading ? <p>Načítavam...</p> : documents.length === 0 ? (
            <div className="dash-table-wrap"><div className="dash-empty">
              {VIEW_TITLES[view].empty}
              {/* Fronta je prázdna, ale v inom okruhu tej istej osoby niečo čaká. */}
              {view === 'queue' && linkedAccounts.some(a => !a.current && a.pending > 0) && (
                <div className="dash-next">
                  {linkedAccounts.filter(a => !a.current && a.pending > 0).map(a => (
                    <button key={a.userId} className="dash-btn is-outline"
                      onClick={() => switchAccount(a.userId).then(() => { window.location.href = '/dashboard'; }).catch(() => setError('Prepnutie sa nepodarilo.'))}>
                      {a.tenantName}: čaká {a.pending} →
                    </button>
                  ))}
                </div>
              )}
            </div></div>
          ) : (
            <>
              <DocumentsTable
                docs={documents}
                onOpen={openModal}
                selectable={canActOn}
                selectedIds={selectedIds}
                onToggle={toggleSelect}
                showCheckboxes={BULK_ACTIONS_ENABLED && selectableDocs.length > 0}
              />
              {hasMore && (
                <div style={{ textAlign: 'center' }}>
                  <button className="dash-btn is-outline" style={{ marginTop: '0.75rem' }}
                    onClick={() => setPageSize(p => p + 50)} disabled={loading}>
                    Načítať ďalšie
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* MODÁL */}
      {selectedDoc && (
        <div
          onClick={closeModal}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
        >
          <div
            className="doc-modal"
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '12px', width: '95vw', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          >
            {/* Modál header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.1rem' }}>
                {selectedDoc.vendor || selectedDoc.original_filename || 'Dokument'}
              </h2>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>

            {/* Hlavný obsah — dve kolumny */}
            <div className="doc-modal-body" style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

              {/* ĽAVÁ STRANA — náhľad dokumentu */}
              <div className="doc-modal-preview" style={{ flex: '0 0 55%', backgroundColor: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {!selectedDoc.file_path || fileAvailable === false ? (
                  <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>
                    <p style={{ fontSize: '3rem', margin: '0 0 1rem' }}>⚠️</p>
                    <p style={{ fontSize: '1.1rem', margin: '0 0 0.5rem', color: '#f3f4f6' }}>Súbor nie je dostupný</p>
                    <p style={{ fontSize: '0.875rem', margin: 0, color: '#9ca3af' }}>
                      Originálny súbor bol vymazaný alebo presunutý do archívu.
                    </p>
                  </div>
                ) : fileAvailable === null ? (
                  <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>
                    <p>Načítavam...</p>
                  </div>
                ) : isPdf(selectedDoc.original_filename) && isMobile && pdfPagesState !== 'error' ? (
                  /* Mobil: strany ako obrázky — celá faktúra na šírku displeja, scrolluje
                     sa spolu s detailmi, pinch-zoom funguje natívne. */
                  <div className="pdf-pages">
                    {pdfPages.map((u, i) => (
                      <img key={i} src={u} alt={`Strana ${i + 1}`} style={{ width: '100%', height: 'auto', display: 'block', background: '#fff', marginBottom: 6 }} />
                    ))}
                    {pdfPagesState === 'loading' && (
                      <div style={{ color: '#9ca3af', textAlign: 'center', padding: '1rem', fontSize: '0.85rem' }}>
                        {pdfPages.length ? 'Načítavam ďalšie strany…' : 'Pripravujem náhľad…'}
                      </div>
                    )}
                    <a href={previewUrl || ''} target="_blank" rel="noreferrer"
                      style={{ display: 'block', textAlign: 'center', padding: '0.5rem', color: '#93c5fd', fontSize: '0.8rem' }}>
                      Otvoriť PDF v prehliadači
                    </a>
                  </div>
                ) : isPdf(selectedDoc.original_filename) ? (
                  <iframe
                    src={previewUrl || ''}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Náhľad dokumentu"
                  />
                ) : isImage(selectedDoc.original_filename) ? (
                  <img
                    src={previewUrl || ''}
                    alt="Náhľad dokumentu"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>
                    <p style={{ fontSize: '3rem', margin: '0 0 1rem' }}>📄</p>
                    <p>Náhľad nie je dostupný</p>
                    <a href={previewUrl || ''} target="_blank" rel="noreferrer"
                      style={{ color: '#60a5fa', textDecoration: 'underline' }}>
                      Otvoriť súbor
                    </a>
                  </div>
                )}
              </div>

              {/* PRAVÁ STRANA — detaily + akcie */}
              <div className="doc-modal-details" style={{ flex: '0 0 45%', overflowY: 'auto', padding: '1.5rem 1.5rem 2rem 1.5rem', borderLeft: '1px solid #e5e7eb', boxSizing: 'border-box' }}>

                {/* Os priebehu — kde doklad je a u koho leží */}
                <ApprovalTimeline doc={selectedDoc} history={history} skipReason={levelReasonText(selectedDoc)} />

                {/* Status */}
                <div style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ padding: '0.35rem 1rem', borderRadius: '999px', backgroundColor: statusColor(selectedDoc.status) + '20', color: statusColor(selectedDoc.status), fontWeight: 600, fontSize: '0.875rem' }}>
                    {statusLabel(selectedDoc.status, selectedDoc.required_level)}
                  </span>
                  {selectedDoc.submitted_by_email === 'peppol' && (
                    <button
                      title="Originál e-faktúry (UBL XML) — dá sa naimportovať do účtovného systému. PDF je len náhľad."
                      onClick={async () => {
                        try {
                          const r = await api.get(`/documents/${selectedDoc.id}/ubl`, { responseType: 'blob' });
                          const url = URL.createObjectURL(r.data);
                          const a = document.createElement('a');
                          a.href = url; a.download = `${selectedDoc.invoice_number || 'efaktura'}.xml`; a.click();
                          setTimeout(() => URL.revokeObjectURL(url), 30000);
                        } catch { alert('XML sa nepodarilo stiahnuť'); }
                      }}
                      style={{ padding: '0.35rem 0.85rem', borderRadius: '999px', backgroundColor: '#ede9fe', color: '#6d28d9', fontWeight: 600, fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>
                      ⚡ e-faktúra · stiahnuť XML
                    </button>
                  )}
                  {selectedDoc.ocr_warning && (
                    <span title={selectedDoc.ocr_warning} style={{ padding: '0.35rem 0.85rem', borderRadius: '999px', backgroundColor: '#fef3c7', color: '#b45309', fontWeight: 600, fontSize: '0.8rem' }}>
                      ⚠ OCR rozpor — overiť sumu
                    </span>
                  )}
                  {selectedDoc.is_proforma && (
                    <span title="Nedaňový doklad — nezahrnutý do Omega exportu" style={{ padding: '0.35rem 0.85rem', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#1e40af', fontWeight: 600, fontSize: '0.8rem' }}>
                      Nedaňový doklad
                    </span>
                  )}
                </div>
                {selectedDoc.ocr_warning && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.85rem', color: '#92400e' }}>
                    {selectedDoc.ocr_warning}
                  </div>
                )}
                {/* Možný duplikát: rovnaký doklad už v systéme je (iný súbor, rovnaký obsah).
                    Auto-schválenie bolo preskočené — schvaľovateľ rozhodne (zamietne alebo potvrdí). */}
                {selectedDoc.duplicate_of_id && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '0.85rem', color: '#991b1b' }}>
                    <b>⚠ Možný duplikát</b> — {selectedDoc.duplicate_reason || 'rovnaký doklad už v systéme je'}.
                    {selectedDoc.duplicate_of && (
                      <>
                        {' '}Pôvodný: <b>{selectedDoc.duplicate_of.vendor || '?'}</b>
                        {selectedDoc.duplicate_of.invoice_number ? ` č. ${selectedDoc.duplicate_of.invoice_number}` : ''}
                        {selectedDoc.duplicate_of.amount != null ? `, ${Number(selectedDoc.duplicate_of.amount).toFixed(2)} ${selectedDoc.duplicate_of.currency || 'EUR'}` : ''}
                        {selectedDoc.duplicate_of.created_at ? `, prijatý ${new Date(selectedDoc.duplicate_of.created_at).toLocaleDateString('sk-SK')}` : ''}
                        {selectedDoc.duplicate_of.status ? ` (${statusLabel(selectedDoc.duplicate_of.status)})` : ''}.
                        <button type="button" onClick={() => openModal(selectedDoc.duplicate_of as unknown as Document)}
                          style={{ marginLeft: '0.6rem', padding: '0.25rem 0.7rem', borderRadius: '999px', border: '1px solid #fca5a5', background: '#fff', color: '#991b1b', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
                          Otvoriť pôvodný
                        </button>
                      </>
                    )}
                    {' '}Ak je to naozaj ten istý doklad, zamietni ho; ak nie, pokračuj v schvaľovaní.
                  </div>
                )}
                {levelReasonText(selectedDoc) && !(selectedDoc.required_level && parseChain(selectedDoc.required_level)[0] > 1 && !selectedDoc.auto_approved) && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '0.85rem', color: '#0c4a6e' }}>
                    ⓘ {levelReasonText(selectedDoc)}
                  </div>
                )}

                {/* Detail info */}
                {user.role === 'admin' && (
                  <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                    {editing ? (
                      <>
                        <button onClick={saveEdit} disabled={editSaving}
                          style={{ padding: '0.4rem 1rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', opacity: editSaving ? 0.6 : 1 }}>
                          {editSaving ? 'Ukladám...' : 'Uložiť'}
                        </button>
                        <button onClick={() => setEditing(false)}
                          style={{ padding: '0.4rem 1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}>
                          Zrušiť
                        </button>
                      </>
                    ) : (
                      <button onClick={startEdit}
                        style={{ padding: '0.4rem 1rem', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}>
                        Upraviť údaje
                      </button>
                    )}
                  </div>
                )}

                <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                  {editing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div>
                        <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dodávateľ</p>
                        <input value={editFields.vendor} onChange={e => setEditFields({ ...editFields, vendor: e.target.value })}
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <div style={{ flex: 2 }}>
                          <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suma bez DPH</p>
                          <input type="number" value={editFields.amount} onChange={e => setEditFields({ ...editFields, amount: e.target.value })}
                            style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mena</p>
                          <input value={editFields.currency} onChange={e => setEditFields({ ...editFields, currency: e.target.value })}
                            style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dátum faktúry</p>
                        <input type="date" value={editFields.invoice_date} onChange={e => setEditFields({ ...editFields, invoice_date: e.target.value })}
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kategória</p>
                        <input value={editFields.category} onChange={e => setEditFields({ ...editFields, category: e.target.value })}
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Opis</p>
                        <textarea value={editFields.description} onChange={e => setEditFields({ ...editFields, description: e.target.value })}
                          rows={3} style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div>
                      <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dodávateľ</p>
                      <p style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>{selectedDoc.vendor || '—'}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suma bez DPH</p>
                      <p style={{ margin: 0, fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>
                        {selectedDoc.amount ? `${selectedDoc.amount} ${selectedDoc.currency}` : '—'}
                      </p>
                    </div>
                    {selectedDoc.invoice_date && (
                      <div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dátum faktúry</p>
                        <p style={{ margin: 0, color: '#1f2937' }}>{new Date(selectedDoc.invoice_date).toLocaleDateString('sk-SK')}</p>
                      </div>
                    )}
                    <div>
                      <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kategória</p>
                      <p style={{ margin: 0, color: '#1f2937' }}>{selectedDoc.category || '—'}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Opis</p>
                      <p style={{ margin: 0, color: '#1f2937' }}>{selectedDoc.description || '—'}</p>
                    </div>
                    {selectedDoc.submitted_by_email && (
                      <div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Odosielateľ</p>
                        <p style={{ margin: 0, color: '#1f2937', fontSize: '0.875rem' }}>{selectedDoc.submitted_by_email}</p>
                      </div>
                    )}
                    {(selectedDoc.stredisko || selectedDoc.zakazka || selectedDoc.ecv) && (
                      <div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pečiatka (stredisko / zákazka / EČV)</p>
                        <p style={{ margin: 0, color: '#1f2937', fontSize: '0.875rem' }}>
                          {[selectedDoc.stredisko, selectedDoc.zakazka, selectedDoc.ecv].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    )}
                    {/* Modul Objednávky — prepojenie na internú objednávku */}
                    {selectedDoc.linked_order && (
                      <div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interná objednávka</p>
                        <p style={{ margin: 0, fontSize: '0.875rem' }}>
                          <a href={`/orders?order=${selectedDoc.order_id}`} style={{ color: '#0891b2', fontWeight: 600, textDecoration: 'none' }}>
                            {selectedDoc.linked_order.order_number}
                          </a>
                          <span style={{ color: '#6b7280' }}> · {selectedDoc.linked_order.subject || ''}</span>
                        </p>
                      </div>
                    )}
                    {!selectedDoc.linked_order && selectedDoc.order_ref && hasOrders && (
                      <div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Číslo objednávky na faktúre</p>
                        <p style={{ margin: 0, color: '#b45309', fontSize: '0.875rem' }}>
                          {selectedDoc.order_ref} <span style={{ color: '#9ca3af' }}>(nespárované so žiadnou schválenou objednávkou)</span>
                        </p>
                      </div>
                    )}
                    <div>
                      <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Súbor</p>
                      <p style={{ margin: 0, color: '#1f2937', fontSize: '0.875rem' }}>{selectedDoc.original_filename}</p>
                      {(selectedDoc.source_files || []).filter(f => f.role === 'attachment').map((f, i) => (
                        <p key={i} style={{ margin: '0.15rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
                          + príloha: {f.filename}{f.pages ? ` (${f.pages} ${f.pages === 1 ? 'strana' : f.pages < 5 ? 'strany' : 'strán'})` : ''}
                        </p>
                      ))}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 0.2rem', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pridané</p>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>{new Date(selectedDoc.created_at).toLocaleString('sk-SK')}</p>
                    </div>
                  </div>
                  )}
                </div>

                {/* Modul Objednávky — manuálne prepojenie/odpojenie s internou objednávkou */}
                {hasOrders && (['accountant', 'admin'].includes(user.role) || user.isSuperAdmin || levelFromRole(user.role) !== null) && (
                  <div style={{ marginBottom: '1.25rem', padding: '0.75rem', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', fontSize: '0.85rem' }}>
                    {selectedDoc.linked_order ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>
                          Prepojené s objednávkou{' '}
                          <a href={`/orders?order=${selectedDoc.order_id}`} style={{ color: '#0891b2', fontWeight: 700, textDecoration: 'none' }}>
                            {selectedDoc.linked_order.order_number}
                          </a>
                          {' '}({Number(selectedDoc.linked_order.amount || 0).toLocaleString('sk-SK', { minimumFractionDigits: 2 })} {selectedDoc.linked_order.currency || 'EUR'})
                        </span>
                        <button onClick={() => linkOrder(selectedDoc.id, null)} disabled={linkSaving}
                          style={{ padding: '3px 10px', background: '#fff', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                          Odpojiť
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ color: '#0f766e' }}>Prepojiť s internou objednávkou:</span>
                          {linkSuggested === null && (
                            <button onClick={() => loadLinkSuggestions(selectedDoc)}
                              style={{ padding: '3px 10px', background: '#fff', color: '#0891b2', border: '1px solid #99f6e4', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>
                              Vybrať objednávku…
                            </button>
                          )}
                        </div>
                        {linkSuggested !== null && (
                          <div style={{ marginTop: 8 }}>
                            {linkSuggested.length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0f766e', textTransform: 'uppercase', marginBottom: 4 }}>
                                  Návrhy podľa dodávateľa
                                </div>
                                {linkSuggested.map((o: any) => (
                                  <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#fff', border: '1px solid #ccfbf1', borderRadius: 4, marginBottom: 4 }}>
                                    <span style={{ fontSize: '0.8rem' }}>
                                      <b>{o.order_number}</b> · {o.supplier_name} · {Number(o.amount || 0).toLocaleString('sk-SK')} {o.currency || 'EUR'}
                                    </span>
                                    <button onClick={() => linkOrder(selectedDoc.id, o.id)} disabled={linkSaving}
                                      style={{ padding: '3px 10px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                      Prepojiť
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <input value={linkQuery} onChange={e => setLinkQuery(e.target.value)}
                              placeholder={linkSuggested.length === 0 ? 'Žiadne návrhy — hľadaj číslo OBJ, dodávateľa, predmet…' : 'Hľadať inú: číslo OBJ, dodávateľ, predmet…'}
                              style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.8rem' }} />
                            {linkQuery.trim().length >= 2 && (
                              linkResults.length === 0 ? (
                                <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: 4 }}>žiadna schválená otvorená objednávka</div>
                              ) : (
                                <div style={{ marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
                                  {linkResults.map((o: any) => (
                                    <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, marginBottom: 4 }}>
                                      <span style={{ fontSize: '0.8rem' }}>
                                        <b>{o.order_number}</b> · {o.supplier_name} · {Number(o.amount || 0).toLocaleString('sk-SK')} {o.currency || 'EUR'}
                                      </span>
                                      <button onClick={() => linkOrder(selectedDoc.id, o.id)} disabled={linkSaving}
                                        style={{ padding: '3px 10px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        Prepojiť
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Príloha k inej faktúre (výkaz, dodací list…) — zlúči sa do jej PDF */}
                {canAccounting && !['approved', 'rejected'].includes(selectedDoc.status) && !selectedDoc.dispatched_at
                  && /\.pdf$/i.test(selectedDoc.original_filename || '') && (
                  <div style={{ marginBottom: '1.25rem', padding: '0.75rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.85rem' }}>
                    {attachCandidates === null ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ color: '#374151' }}>Je to len príloha k inej faktúre?</span>
                        <button onClick={() => loadAttachCandidates(selectedDoc.id)}
                          style={{ padding: '3px 10px', background: '#fff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>
                          Pripojiť k faktúre…
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ color: '#374151', marginBottom: 6 }}>
                          Vyber faktúru — tento doklad sa pridá za jej strany a samostatne zanikne:
                        </div>
                        <input value={attachQuery} onChange={e => setAttachQuery(e.target.value)}
                          placeholder="Hľadať: dodávateľ, číslo faktúry, súbor…"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.8rem', marginBottom: 6 }} />
                        {attachCandidates.length === 0 ? (
                          <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>žiadna neschválená faktúra</div>
                        ) : (
                          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                            {attachCandidates.map((d: any) => (
                              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, marginBottom: 4 }}>
                                <span style={{ fontSize: '0.8rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                                  <b>{d.vendor || d.original_filename}</b>
                                  {d.invoice_number ? ` č. ${d.invoice_number}` : ''}
                                  {d.amount != null ? ` · ${Number(d.amount).toLocaleString('sk-SK', { minimumFractionDigits: 2 })} ${d.currency || 'EUR'}` : ''}
                                  {d.same_email && <span style={{ color: '#059669' }}> · z toho istého mailu</span>}
                                </span>
                                <button onClick={() => attachTo(d)} disabled={attachSaving}
                                  style={{ padding: '3px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: attachSaving ? 'wait' : 'pointer', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  Pripojiť
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* B15: nedaňový doklad (proforma) — účtovník/admin prepína účasť v Omega exporte */}
                {(['accountant', 'admin'].includes(user.role) || user.isSuperAdmin) && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '1.25rem', padding: '0.75rem', backgroundColor: selectedDoc.is_proforma ? '#eff6ff' : '#f9fafb', border: `1px solid ${selectedDoc.is_proforma ? '#bfdbfe' : '#e5e7eb'}`, borderRadius: '8px', cursor: proformaSaving ? 'wait' : 'pointer', fontSize: '0.85rem' }}>
                    <input
                      type="checkbox"
                      checked={!!selectedDoc.is_proforma}
                      disabled={proformaSaving}
                      onChange={e => toggleProforma(e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span style={{ color: '#374151' }}>
                      <b>Nedaňový doklad</b> (proforma / zálohová / predfaktúra)
                      <span style={{ display: 'block', color: '#6b7280', fontSize: '0.78rem', marginTop: 2 }}>
                        Nezahrnie sa do Omega účtovného exportu — zaúčtuje sa až ostrá faktúra.
                      </span>
                    </span>
                  </label>
                )}

                {/* DPH rozpis po sadzbách (multi-rate) */}
                <VatBreakdownPanel
                  breakdown={selectedDoc.vat_breakdown}
                  baseZero={selectedDoc.vat_base_zero}
                  baseNeobsahuje={selectedDoc.vat_base_neobsahuje}
                  totalAmount={selectedDoc.total_amount}
                  currency={selectedDoc.currency}
                />

                {/* Klasifikácia položiek (resale detection) */}
                {hasResaleDetection && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: '0 0 0.5rem', color: '#1f2937', fontSize: '0.95rem' }}>Položky a klasifikácia</h3>
                    <DocumentItemsPanel docId={selectedDoc.id} vendor={selectedDoc.vendor} currency={selectedDoc.currency} />
                  </div>
                )}

                {/* História udalostí */}
                <h3 style={{ margin: '0 0 0.75rem', color: '#1f2937', fontSize: '0.95rem' }}>História udalostí</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  {/* Najnovšie udalosti hore — schválenia v obrátenom poradí */}
                  {historyLoading ? (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Načítavam...</p>
                  ) : (
                    [...history].reverse().map(entry => {
                      // B8: out-of-flow akcie účtovníka — neutrálny sivý štýl,
                      // bez "— L0" za názvom (level je sentinel).
                      const isAccountantAction = entry.action === 'accountant_handled' || entry.action === 'accountant_unhandled';
                      const bg = isAccountantAction ? '#f9fafb'
                        : entry.action === 'approved' ? '#f0fdf4'
                        : entry.action === 'returned' ? '#fffbeb' : '#fef2f2';
                      const accent = isAccountantAction ? '#6b7280'
                        : entry.action === 'approved' ? '#16a34a'
                        : entry.action === 'returned' ? '#b45309' : '#dc2626';
                      return (
                        <div key={entry.id} style={{ padding: '0.75rem', backgroundColor: bg, borderRadius: '6px', borderLeft: `3px solid ${accent}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                            <span style={{ fontWeight: 600, color: accent, fontSize: '0.8rem' }}>
                              {actionLabel(entry.action)}{isAccountantAction ? '' : ` — L${entry.level}`}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                              {new Date(entry.created_at).toLocaleString('sk-SK')}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#374151' }}>{entry.user_name}</p>
                          {entry.comment && (
                            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>„{entry.comment}"</p>
                          )}
                        </div>
                      );
                    })
                  )}

                  {/* Doručenie — vždy najstaršia, teda na spodku */}
                  <div style={{ padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '6px', borderLeft: '3px solid #2563eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 600, color: '#2563eb', fontSize: '0.8rem' }}>
                        {selectedDoc.email_message_id ? 'Doručený emailom' : 'Nahraté cez UI'}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        {new Date(selectedDoc.created_at).toLocaleString('sk-SK')}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#374151' }}>{selectedDoc.submitted_by_email}</p>
                  </div>
                </div>

                {/* Akcia */}
                {canAct && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                    <h3 style={{ margin: '0 0 0.75rem', color: '#1f2937', fontSize: '0.95rem' }}>Tvoja akcia</h3>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.875rem' }}>Komentár (voliteľné)</label>
                      <textarea
                        value={actionComment}
                        onChange={e => setActionComment(e.target.value)}
                        placeholder="Dôvod schválenia alebo zamietnutia..."
                        rows={2}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>
                    {/* Pečiatka do ľavého horného rohu PDF — voliteľné */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.875rem' }}>Pečiatka na doklad (voliteľné)</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                        <input
                          value={stampStredisko}
                          onChange={e => setStampStredisko(e.target.value)}
                          placeholder="Stredisko"
                          maxLength={48}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                        />
                        <input
                          value={stampZakazka}
                          onChange={e => setStampZakazka(e.target.value)}
                          placeholder="Zákazka"
                          maxLength={48}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                        />
                        <input
                          value={stampEcv}
                          onChange={e => setStampEcv(e.target.value)}
                          placeholder="EČV"
                          maxLength={48}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' , marginBottom: '1rem' }}>
                      <button onClick={() => handleAction('approve')} disabled={actionLoading}
                        style={{ flex: 1, padding: '0.75rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, opacity: actionLoading ? 0.6 : 1 }}>
                        ✓ Schváliť
                      </button>
                      <button onClick={() => handleAction('reject')} disabled={actionLoading}
                        style={{ flex: 1, padding: '0.75rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, opacity: actionLoading ? 0.6 : 1 }}>
                        ✗ Zamietnuť
                      </button>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <button onClick={openReturnModal} disabled={actionLoading}
                        style={{ width: '100%', padding: '0.65rem', backgroundColor: 'white', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, opacity: actionLoading ? 0.6 : 1 }}>
                        ↩ Vrátiť na opravu
                      </button>
                    </div>
                  </div>
                )}

                {/* B8: stav vybavenia zamietnutého dokladu.
                    — pre účtovníka: aktívny toggle (označiť / vrátiť späť).
                    — pre admina/superadmina: read-only info badge keď je vybavené. */}
                {selectedDoc.status === 'rejected' && (
                  user.role === 'accountant' ? (
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
                      <h3 style={{ margin: '0 0 0.5rem', color: '#1f2937', fontSize: '0.95rem' }}>Stav vybavenia</h3>
                      {selectedDoc.accountant_handled_at ? (
                        <>
                          <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                            ✓ Vybavené {new Date(selectedDoc.accountant_handled_at).toLocaleString('sk-SK')}
                          </p>
                          <button onClick={() => toggleHandled(false)} disabled={handleSaving}
                            style={{ width: '100%', padding: '0.6rem', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem', opacity: handleSaving ? 0.6 : 1 }}>
                            Vrátiť medzi nevybavené
                          </button>
                        </>
                      ) : (
                        <button onClick={() => toggleHandled(true)} disabled={handleSaving}
                          style={{ width: '100%', padding: '0.75rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: handleSaving ? 0.6 : 1 }}>
                          {handleSaving ? 'Ukladám...' : '✓ Označiť ako vybavené'}
                        </button>
                      )}
                    </div>
                  ) : (user.role === 'admin' || user.isSuperAdmin) && selectedDoc.accountant_handled_at ? (
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        ✓ Účtovník označil ako vybavené {new Date(selectedDoc.accountant_handled_at).toLocaleString('sk-SK')}
                      </p>
                    </div>
                  ) : null
                )}

                {/* Vymazanie dokladu — len admin */}
                {user.role === 'admin' && (
                  <div style={{ borderTop: '1px solid #fee2e2', paddingTop: '1rem', marginTop: '1rem' }}>
                    <button onClick={deleteDocument}
                      style={{ width: '100%', padding: '0.75rem', backgroundColor: 'white', color: '#dc2626', border: '1px solid #dc2626', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                      Vymazať doklad
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Omega export modál */}
      {showOmegaExport && (
        <OmegaExportModal onClose={() => setShowOmegaExport(false)} />
      )}

      {/* Manager vystavených faktúr (priradenie účtov) */}
      {showOutputInvoices && (
        <OutputInvoicesManager onClose={() => setShowOutputInvoices(false)} />
      )}

      {/* Upload modál */}
      {showUpload && (
        <div onClick={() => setShowUpload(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '12px', width: '480px', maxWidth: '100%', padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937' }}>Nahrať doklad</h2>
              <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                  e.preventDefault();
                  setDragOver(false);
                  const files = Array.from(e.dataTransfer.files).filter(f => /\.pdf$/i.test(f.name));
                  if (files.length > 0) setUploadFiles(files);
                }}
                onClick={() => document.getElementById('upload-file-input')?.click()}
                style={{
                  border: `2px dashed ${dragOver ? '#2563eb' : uploadFiles.length > 0 ? '#16a34a' : '#d1d5db'}`,
                  borderRadius: '8px',
                  padding: '2rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: dragOver ? '#eff6ff' : uploadFiles.length > 0 ? '#f0fdf4' : '#f9fafb',
                  transition: 'all 0.15s'
                }}
              >
                <input id="upload-file-input" type="file" accept=".pdf,application/pdf" multiple
                  onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                  style={{ display: 'none' }} />
                <p style={{ margin: '0 0 0.25rem', fontSize: '1.5rem' }}>{uploadFiles.length > 0 ? '✓' : '📄'}</p>
                <p style={{ margin: 0, fontWeight: 600, color: uploadFiles.length > 0 ? '#16a34a' : '#374151' }}>
                  {uploadFiles.length > 1 ? `${uploadFiles.length} súborov vybraných` : uploadFiles.length === 1 ? uploadFiles[0].name : 'Pretiahni súbory sem'}
                </p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>
                  {uploadFiles.length > 0 ? 'Klikni pre zmenu' : 'alebo klikni pre výber — len PDF'}
                </p>
              </div>
              {uploading && uploadProgress && (
                <p style={{ margin: 0, color: '#2563eb', fontSize: '0.875rem', textAlign: 'center' }}>
                  Nahrávam {uploadProgress.done + 1} z {uploadProgress.total}...
                </p>
              )}
              <button onClick={submitUpload} disabled={uploadFiles.length === 0 || uploading}
                style={{ padding: '0.75rem', backgroundColor: uploadFiles.length === 0 || uploading ? '#9ca3af' : '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: uploadFiles.length === 0 || uploading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.95rem' }}>
                {uploading ? `Nahrávam... (${uploadProgress?.done}/${uploadProgress?.total})` : `Nahrať ${uploadFiles.length > 1 ? uploadFiles.length + ' dokladov' : 'doklad'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress banner - nahrávanie na pozadí */}
      {uploading && uploadProgress && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem',
          backgroundColor: '#1e40af', color: 'white',
          padding: '0.875rem 1.25rem', borderRadius: '10px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          zIndex: 9999, fontSize: '0.9rem', fontWeight: 500
        }}>
          <div style={{
            width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.4)',
            borderTop: '2px solid white', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <span>
            Nahrávam {uploadProgress.done + 1} z {uploadProgress.total}
            {uploadProgress.total > 1 && ` — dokončených ${uploadProgress.done}`}
          </span>
        </div>
      )}
      {/* Po nahratí: OCR beží na pozadí, nič sa nenahráva → bez spinnera */}
      {!uploading && processing.length > 0 && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem',
          backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
          padding: '0.875rem 1.25rem', borderRadius: '10px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 9999, fontSize: '0.9rem', fontWeight: 500, maxWidth: 'calc(100vw - 3rem)'
        }}>
          ✓ Nahraté. {processing.length === 1 ? 'Doklad sa rozpoznáva' : processing.length < 5 ? `${processing.length} doklady sa rozpoznávajú` : `${processing.length} dokladov sa rozpoznáva`}, v zozname sa objavia samé.
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Vrátenie na opravu — dôvod je povinný. Keď doklad nemá kam ísť nižšie,
          ukážeme komu a s akým textom pôjde odpoveď, ešte pred odoslaním. */}
      {returnModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem'
        }} onClick={() => { if (!returnSaving) setReturnModal(null); }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: 'white', borderRadius: '10px', padding: '1.5rem',
            width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
          }}>
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#1f2937' }}>Vrátiť na opravu</h2>

            {returnModal.loading && <p style={{ color: '#6b7280' }}>Pripravujem…</p>}

            {!returnModal.loading && returnModal.preview && (
              <>
                {returnModal.preview.mode === 'level' ? (
                  <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#374151' }}>
                    Doklad sa vráti na úroveň <b>L{returnModal.preview.target_level}</b> aj s dôvodom, ktorý uvedieš.
                  </p>
                ) : returnModal.preview.can_email ? (
                  <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#374151' }}>
                    Nižšia úroveň neexistuje, preto sa vrátením <b>odošle odpoveď na {returnModal.preview.to}</b>
                    {returnModal.preview.threaded ? ' do pôvodného emailového vlákna' : ''}. Text nižšie si môžeš upraviť.
                  </p>
                ) : (
                  <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#b45309', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.6rem' }}>
                    Doklad nemá emailového odosielateľa, takže sa nemá komu poslať odpoveď. Doklad sa označí ako vrátený, ale nikomu nič neodíde.
                  </p>
                )}

                <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.875rem' }}>
                  Dôvod vrátenia <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={returnModal.reason}
                  onChange={e => setReturnReason(e.target.value)}
                  rows={3}
                  placeholder="Napr. chýba objednávkové číslo, nesedí suma…"
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />

                {returnModal.preview.mode === 'email' && returnModal.preview.can_email && (
                  <div style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.875rem' }}>Predmet odpovede</label>
                    <input
                      value={returnModal.subject}
                      onChange={e => setReturnModal(m => m && ({ ...m, subject: e.target.value }))}
                      style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '0.75rem' }}
                    />
                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.875rem' }}>Text odpovede</label>
                    <textarea
                      value={returnModal.body}
                      onChange={e => setReturnModal(m => m && ({ ...m, body: e.target.value, bodyDirty: true }))}
                      rows={10}
                      style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                  <button onClick={() => setReturnModal(null)} disabled={returnSaving}
                    style={{ flex: 1, padding: '0.7rem', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
                    Zrušiť
                  </button>
                  <button onClick={submitReturn} disabled={returnSaving || !returnModal.reason.trim()}
                    style={{ flex: 1, padding: '0.7rem', backgroundColor: '#b45309', color: 'white', border: 'none', borderRadius: '6px', cursor: returnModal.reason.trim() ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: (returnSaving || !returnModal.reason.trim()) ? 0.6 : 1 }}>
                    {returnModal.preview?.mode === 'email' && returnModal.preview.can_email ? '↩ Vrátiť a odoslať odpoveď' : '↩ Vrátiť na opravu'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}